/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import SearchInput from '../../components/SearchInput/SearchInput'
import DefaultLoader from '../../components/Loaders/DefaultLoader'
import DeliveryItem from './DeliveryItem'
import { SearchOptions } from '../../components/OrderOverview/OrderOverview'
import Pagination from '../../components/Pagination/Pagination'
import { OrderDelivery } from '../../model'
import { getSearchParamsAsOb } from '../../helpers/utils'
import { getOrderDetailDeliveries } from '../../services/api/orders'
import { getOrderDeliveries } from '../../services/api/deliveries'
import { useUpdateEffect } from 'react-use'

export interface Delivery {
    id: string
    created: string
    updated: string
    message: string
    order: any
    parent_order: any
}

interface EnabledSearchParams {
    s?: string | undefined
    page?: string | undefined
}

const defaultSearchOptions = {
    page: 1,
    pageSize: 10,
    searchString: ''
}

const DynamicDeliveries: React.FunctionComponent<{ canSearch?: boolean }> = ({ canSearch = false }) => {
    const { orderId } = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const [loading, setLoading] = useState<boolean>(false)

    const [searchOptions, setSearchOptions] = useState<SearchOptions>({
        ...defaultSearchOptions
    })
    const onOrderDetail = orderId !== undefined

    const [deliveries, setDeliveries] = useState<OrderDelivery[]>([])
    const [total, setTotal] = useState<number>(0)

    const getDeliveries = async () => {
        setLoading(true)

        const params = {
            ...searchOptions
        }

        try {
            let res: any = null
            if (onOrderDetail) {
                res = await getOrderDetailDeliveries(parseInt(orderId), params)
            } else {
                res = await getOrderDeliveries(params)
            }

            setDeliveries(res?.items || [])
            setTotal(res?.total || 0)
        } catch (e) {
            console.error(e)
            setDeliveries([])
            setTotal(0)
        } finally {
            setLoading(false)
        }
    }

    const updateSearchParams = (params: EnabledSearchParams) => {
        const paramsToSet = Object.fromEntries(Object.entries(params).filter(([_, value]) => value !== undefined))
        setSearchParams(paramsToSet)
    }

    const handleSearchChange = (query: string) => {
        const searchParamsObj = getSearchParamsAsOb(searchParams)
        let params: EnabledSearchParams = { ...searchParamsObj, s: query || undefined }

        // Reset page to first when needed
        if (searchParams.has('page')) {
            params.page = '1'
        }
        updateSearchParams(params)
    }

    const handlePageChange = (page: number) => {
        const searchParamsObj = getSearchParamsAsOb(searchParams)
        const params: EnabledSearchParams = { ...searchParamsObj, page }
        updateSearchParams(params)
    }

    const updateSearchOptions = () => {
        setSearchOptions({
            ...searchOptions,
            searchString: searchParams.get('s') || undefined,
            page: searchParams.get('page') ? parseInt(searchParams.get('page') || '1') : 1
        })
    }

    useEffect(() => {
        // Initialize based on search params or just load the first results
        if (searchParams.has('page') || searchParams.has('s')) {
            updateSearchOptions()
        } else {
            getDeliveries()
        }
    }, [])

    useUpdateEffect(() => {
        getDeliveries()
    }, [searchOptions])

    useUpdateEffect(() => {
        updateSearchOptions()
    }, [searchParams])

    return (
        <>
            {canSearch && (
                <div className='flex justify-right flex-wrap px-2 py-6 w-full'>
                    <div className='flex items-center justify-start w-full px-2'>
                        <SearchInput
                            initialValue={searchOptions.searchString}
                            placeholder='Zoek op opdracht of locatie'
                            onSearchChange={handleSearchChange}
                            className='w-full max-w-lg'
                        />
                    </div>
                </div>
            )}
            {loading && (
                <div className='w-full h-full top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 items-center flex justify-center py-4 absolute bg-white/85 z-50'>
                    <DefaultLoader loadingMessage='Opleveringen Laden...' />
                </div>
            )}
            {!loading && total === 0 ? (
                <div className='flex items-center justify-center w-full h-auto flex-grow lg:min-h-[500px]'>
                    <div className='text-center text-gray-400 my-auto mx-auto w-full'>Geen opleveringen</div>
                </div>
            ) : (
                <div className='flex-grow'>
                    {deliveries.map((delivery: OrderDelivery) => {
                        return (
                            <DeliveryItem
                                key={delivery.id}
                                data={delivery}
                                onDownload={getDeliveries}
                                onOrderDetail={onOrderDetail}
                            />
                        )
                    })}
                </div>
            )}
            {total > 10 && (
                <div className='px-2 justify-self-end'>
                    <Pagination
                        page={searchOptions.page}
                        itemsPerPage={searchOptions.pageSize}
                        totalItems={total}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}
        </>
    )
}

export default DynamicDeliveries
